import React, { useCallback, useEffect, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { obtenerEmojiBandera } from '../../utils/whatsapp';
import { BookingModal, BookingStep } from './BookingModal';
import { INDUSTRIES, Industry, CatalogShowcaseItem, getIndustryIndexFromSlug } from './industries.data';

function BrowserChrome({ url, children }: { url: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-md overflow-hidden transition-all duration-200">
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 border-b border-gray-200">
        <div className="flex gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#ff5f57' }} />
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#febc2e' }} />
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#28c840' }} />
        </div>
        <div className="flex-1 text-center text-[11px] sm:text-xs text-gray-500 truncate font-mono bg-white/80 rounded-md px-2 py-1 border border-gray-200/80">
          https://{url}
        </div>
      </div>
      {children}
    </div>
  );
}

const WhatsAppGlyph = ({ className = 'w-5 h-5' }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
  </svg>
);

function tipoEtiqueta(t: CatalogShowcaseItem['tipo']): string {
  switch (t) {
    case 'clase':
      return 'Clase';
    case 'membresia':
      return 'Membresía';
    case 'listado':
      return 'Listado';
    case 'producto':
      return 'Producto';
    default:
      return 'Servicio';
  }
}

/**
 * Miniatura del layout real de `CatalogoPublico` + `HeaderEmpresa`:
 * cabecera con acciones, pestañas por vertical, cuadrícula estilo ProductoCard, FAB tipo CatalogChatWidget.
 */
function CatalogPreviewPanel({
  industry,
  onReservar,
  onOpenChatDemo,
}: {
  industry: Industry;
  onReservar: () => void;
  onOpenChatDemo: () => void;
}) {
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    setActiveTab(0);
  }, [industry.id]);

  const openDemoWhatsApp = () => {
    const text = encodeURIComponent(`Hola ${industry.biz}, vi su catálogo en Clientelo.`);
    window.open(`https://wa.me/573001234567?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <BrowserChrome url={industry.siteSlug}>
      <div className="bg-white relative pb-20 min-h-[480px] sm:min-h-[520px]">
        {/* —— Header al estilo HeaderEmpresa (acciones en orden marketing: WhatsApp → Reservar → Chat) —— */}
        <header className="border-b border-gray-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg flex items-center justify-center text-2xl sm:text-3xl shrink-0 border-2 shadow-sm"
                  style={{
                    backgroundColor: industry.accentLight,
                    borderColor: industry.color,
                  }}
                >
                  {industry.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{industry.biz}</h2>
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 flex-wrap">
                    <span>{obtenerEmojiBandera(industry.pais)}</span>
                    <span>
                      {industry.ciudad}, {industry.pais}
                    </span>
                    <span className="hidden sm:inline">•</span>
                    <span className="max-w-[220px] sm:max-w-md truncate hidden sm:inline text-gray-500">
                      {industry.sub}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-row flex-wrap items-center gap-2 md:justify-end">
                <button
                  type="button"
                  onClick={openDemoWhatsApp}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm hover:opacity-95 transition-opacity"
                  style={{ backgroundColor: '#25D366' }}
                >
                  <WhatsAppGlyph className="w-4 h-4" />
                  WhatsApp
                </button>
                <button
                  type="button"
                  onClick={onReservar}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm hover:opacity-95 transition-opacity"
                  style={{ backgroundColor: industry.color }}
                >
                  <span aria-hidden>📅</span>
                  {industry.cta}
                </button>
                <button
                  type="button"
                  onClick={onOpenChatDemo}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border-2 border-gray-200 text-gray-800 hover:bg-gray-50 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" strokeWidth={2.25} />
                  Chatear
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Pestañas (como categorías / módulos en catálogo) */}
        <div className="border-b border-gray-200 px-3 sm:px-4">
          <div className="flex flex-wrap gap-1 -mb-px">
            {industry.catTabs.map((tab, i) => {
              const active = i === activeTab;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(i)}
                  className={`px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    active ? 'text-gray-900' : 'text-gray-500 border-transparent hover:text-gray-700'
                  }`}
                  style={
                    active
                      ? { borderBottomColor: industry.color, color: undefined }
                      : undefined
                  }
                >
                  {tab}
                </button>
              );
            })}
          </div>
        </div>

        {/* Cuadrícula estilo ProductoCard */}
        <div className="p-3 sm:p-4 grid grid-cols-2 gap-2 sm:gap-3">
          {industry.catalogItems.map((item) => (
            <button
              key={item.name}
              type="button"
              onClick={onReservar}
              className="text-left rounded-lg border border-gray-200 bg-white p-2.5 sm:p-3 shadow-sm hover:shadow-md transition-shadow"
            >
              {item.imageUrl ? (
                <div className="h-20 sm:h-24 rounded-md overflow-hidden mb-2 bg-gray-100">
                  <img
                    src={item.imageUrl}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ) : (
                <div
                  className="h-20 sm:h-24 rounded-md flex items-center justify-center text-3xl sm:text-4xl mb-2"
                  style={{ backgroundColor: industry.accentLight }}
                >
                  {item.icon}
                </div>
              )}
              <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
                {tipoEtiqueta(item.tipo)}
              </span>
              <div className="text-xs sm:text-sm font-semibold text-gray-900 line-clamp-2 leading-tight mt-0.5">
                {item.name}
              </div>
              <div className="text-[11px] sm:text-xs text-gray-500 mt-1 line-clamp-2">{item.meta}</div>
              <div className="text-xs sm:text-sm font-bold mt-1.5" style={{ color: industry.color }}>
                {item.price}
              </div>
            </button>
          ))}
        </div>

        {/* FAB tipo CatalogChatWidget */}
        <button
          type="button"
          onClick={onOpenChatDemo}
          className="absolute bottom-4 right-4 w-14 h-14 rounded-full shadow-xl flex items-center justify-center text-2xl text-white z-10 hover:scale-105 transition-transform"
          style={{ backgroundColor: industry.color }}
          aria-label="Abrir chat"
        >
          <span className="relative">
            💬
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white" />
          </span>
        </button>
      </div>
    </BrowserChrome>
  );
}

export interface IndustriesShowcaseProps {
  /** Si viene de `/demo/:slug`, preselecciona la pestaña del sector. */
  initialSlug?: string;
}

export const IndustriesShowcase: React.FC<IndustriesShowcaseProps> = ({ initialSlug }) => {
  const [activeIndustry, setActiveIndustry] = useState(() => getIndustryIndexFromSlug(initialSlug));
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingStep, setBookingStep] = useState<BookingStep>(0);
  const [selectedService, setSelectedService] = useState<number | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [email, setEmail] = useState('');
  const [notas, setNotas] = useState('');

  const resetBooking = useCallback(() => {
    setBookingStep(0);
    setSelectedService(null);
    setSelectedDay(null);
    setSelectedTime(null);
    setNombre('');
    setTelefono('');
    setEmail('');
    setNotas('');
  }, []);

  useEffect(() => {
    setActiveIndustry(getIndustryIndexFromSlug(initialSlug));
  }, [initialSlug]);

  useEffect(() => {
    resetBooking();
    setBookingOpen(false);
  }, [activeIndustry, resetBooking]);

  const industry = INDUSTRIES[activeIndustry];
  if (!industry) return null;

  const openBooking = () => setBookingOpen(true);

  const bookingProps = {
    industry,
    step: bookingStep,
    setStep: setBookingStep,
    selectedService,
    setSelectedService,
    selectedDay,
    setSelectedDay,
    selectedTime,
    setSelectedTime,
    nombre,
    setNombre,
    telefono,
    setTelefono,
    email,
    setEmail,
    notas,
    setNotas,
    onResetBooking: resetBooking,
  };

  return (
    <section id="negocios" className="py-16 sm:py-20 bg-gray-50 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-12 transition-all duration-200">
          <p className="text-xs sm:text-sm font-semibold tracking-widest text-gray-500 uppercase mb-2">
            Diseñado para tu negocio
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Así se ve tu catálogo público con Clientelo
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Misma estructura que tu página en vivo: cabecera, pestañas por rubro, tarjetas y chat.
            Prueba el flujo de reserva al lado.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-8 sm:mb-10">
          {INDUSTRIES.map((ind, i) => {
            const active = i === activeIndustry;
            return (
              <button
                key={ind.id}
                type="button"
                onClick={() => setActiveIndustry(i)}
                className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-sm font-medium border-2 transition-all duration-200 ${
                  active ? 'text-white border-transparent shadow-md' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                }`}
                style={
                  active ? { backgroundColor: ind.color, borderColor: ind.color } : undefined
                }
              >
                <span aria-hidden>{ind.icon}</span>
                <span className="truncate max-w-[10rem] sm:max-w-[14rem] md:max-w-none text-left">
                  {ind.label}
                </span>
              </button>
            );
          })}
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 transition-all duration-200"
          key={industry.id}
        >
          <div>
            <p className="text-sm font-medium text-gray-500 mb-2 text-center md:text-left">
              Vista pública (como CatalogoPublico)
            </p>
            <CatalogPreviewPanel
              industry={industry}
              onReservar={openBooking}
              onOpenChatDemo={openBooking}
            />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500 mb-2 text-center md:text-left">
              Flujo de reserva (demo)
            </p>
            <BrowserChrome url={`${industry.siteSlug}/reservar`}>
              <div className="p-3 sm:p-4 bg-gray-50 min-h-[520px]">
                <BookingModal {...bookingProps} inline />
              </div>
            </BrowserChrome>
          </div>
        </div>
      </div>

      {bookingOpen && (
        <BookingModal
          {...bookingProps}
          inline={false}
          onClose={() => setBookingOpen(false)}
        />
      )}
    </section>
  );
};
